import { initiateApp } from './initiate-app'
import loadingSpinnerGif from '../asset/image/loading-spinner.gif'
import { MainNav } from '../component/nav/main-nav'
import { ChoosePlanModal } from '../component/profile/choose-plan-modal'
import { TrainerDetails } from '../component/trainer/trainer-details'
import { PageFooter } from '../component/util/page-footer'
import { AuthSessionContextProvider } from '../context/auth-session-context'
import { SessionContextProvider } from '../context/session-context'
import { HomeOnboardingAppTourGateProvider } from '../context/home-onboarding-app-tour-gate-context'
import { TrainerModalContextProvider } from '../context/trainer-modal-context'
import { Router } from '../screen/router'
import { authService } from '../service/auth-service'
import { logger } from '../util/logger'
import { AppTour } from '../component/app-tour/app-tour'
import { AppTourController } from '../component/app-tour/app-tour-controller'
import { eventBusController as authEventBusController } from '../controller/auth/rxjs-bus/event-bus-controller'
import { analyticsService } from '../service/analytics-service'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { NextStepProvider } from 'nextstepjs'

const isAuthRoute = (pathname: string): boolean => pathname.startsWith('/auth')

/**
 * Auth layout — rendered for /auth/* routes (login, signup, etc.)
 * No main nav, no footer, no session data loading.
 */
const AuthLayout = (): React.ReactElement => {
  // The auth-side bus (Lead / CompleteRegistration to GA and Meta) had no subscriber: only
  // the core controller was ever registered, and only under CoreLayout. Registered here so
  // the sign-in flow's events reach the pixels — GH-916.
  useEffect(() => {
    authEventBusController().register()
    return () => authEventBusController().unsubscribe()
  }, [])

  return (
    <AuthSessionContextProvider>
      <div className="App">
        <Router />
        <ToastContainer />
      </div>
    </AuthSessionContextProvider>
  )
}

const CoreLayoutContent = (): React.ReactElement => {
  return (
    <AppTour>
      <TrainerModalContextProvider>
        <MainNav />
        <div className="app-flex-content">
          <Router />
          <ChoosePlanModal />
        </div>
        <TrainerDetails />
      </TrainerModalContextProvider>
      <AppTourController />
    </AppTour>
  )
}

/**
 * Core layout — rendered for all other routes (authenticated app)
 * Includes full initialization, nav, footer, session data, etc.
 */
const CoreLayout = (): React.ReactElement => {
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && !authService.hasUserAcceptedLegalAgreements()) {
      navigate('/legal-agreement-check')
    }
  }, [navigate, isLoading])

  useEffect(() => {
    setIsLoading(true)
    initiateApp()
      .then(() => {
        setIsLoading(false)
      })
      .catch((err) => logger.error(err))
  }, [])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen w-screen">
        <img src={loadingSpinnerGif} alt="Loading..." className="w-[200px] h-[200px] object-contain" />
      </div>
    )
  }

  return (
    <div className="App app-flex-root">
      <NextStepProvider>
        <SessionContextProvider>
          <HomeOnboardingAppTourGateProvider>
            <CoreLayoutContent />
          </HomeOnboardingAppTourGateProvider>
        </SessionContextProvider>
      </NextStepProvider>
      <ToastContainer />
      <PageFooter />
    </div>
  )
}

export const App = (): React.ReactElement => {
  const location = useLocation()

  // One `$pageview` per route, for both layouts. The SDK's own pageview is off so the first
  // load is not counted twice.
  useEffect(() => {
    analyticsService.pageview({ path: location.pathname })
  }, [location.pathname])

  if (isAuthRoute(location.pathname)) {
    return <AuthLayout />
  }

  return <CoreLayout />
}
